import * as React from 'react';
import { EntityType, IEntitySessionMetadata } from '../../data/types';
import { infiniteSessions } from '../../data/providers/sessionsProvider';
import { useEffectWhen } from '../useEffectWhen';

interface IInfiniteSessionData {
  sessionsLoading: boolean;
  sessions: IEntitySessionMetadata[];
  setStartAfter: (id: number) => void;
  setEntity: (entity: IEntityDefinition) => void;
}

interface IEntityDefinition {
  entityId: string;
  entityType: EntityType;
}

/**
 * @description returns an object including tests and if the initial load is happening,
 * or if the tests is just an empty array
 */
export const useInfiniteSessions = (
  props: IEntityDefinition
): IInfiniteSessionData => {
  const [sessions, setSessions] = React.useState<IEntitySessionMetadata[]>([]);
  const [startAfter, setStartAfter] = React.useState<number>();
  const [sessionsLoading, setSessionsLoading] = React.useState(true);
  const [entity, setEntity] = React.useState<IEntityDefinition>(props);

  /**
   * @description runs when startAfter changes and append the result to the already fetched data
   */
  useEffectWhen(
    (): void => {
      /// only run if startAt is defined
      if (startAfter !== undefined) {
        setSessionsLoading(true);
        infiniteSessions(entity.entityId, entity.entityType, startAfter).then(
          (data: IEntitySessionMetadata[]): void => {
            setSessions(
              (mSessions: IEntitySessionMetadata[]): IEntitySessionMetadata[] =>
                mSessions.concat(data)
            );
            setSessionsLoading(false);
          }
        );
      }
    },
    [startAfter, entity.entityId, entity.entityType],
    [startAfter]
  );

  /**
   * @description runs when the entity changes and overwrites the already fetched data
   */
  useEffectWhen(
    (): void => {
      if (entity.entityId && entity.entityType) {
        setSessionsLoading(true);
        setSessions([]);
        infiniteSessions(entity.entityId, entity.entityType, undefined).then(
          (data: IEntitySessionMetadata[]): void => {
            setSessions(data);
            setSessionsLoading(false);
          }
        );
      }
    },
    [startAfter, entity.entityId, entity.entityType],
    [entity.entityId, entity.entityType]
  );

  return { sessionsLoading, sessions, setStartAfter, setEntity };
};
